/**
 * One selectable entry of the content-language switcher.
 */
export interface ContentLanguageOption {
  /** Canonical BCP-47 tag as delivered by the API (e.g. `de-DE`, `en`). */
  locale: string;
  /** Short chip text — the language subtag, upper-cased; region is appended only to disambiguate. */
  short: string;
  /** Long, human-readable name in the viewer's UI language (tooltip / aria-label). */
  label: string;
  isSource: boolean;
  isSelected: boolean;
}

export function normalizeLocaleTag(value: string | null | undefined): string | null {
  const candidate = value?.trim().replace(/_/g, '-');
  if (!candidate) {
    return null;
  }
  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? null;
  } catch {
    return null;
  }
}

function languageOf(locale: string): string {
  try {
    return new Intl.Locale(locale).language.toLowerCase();
  } catch {
    return locale.split('-')[0].toLowerCase();
  }
}

function sameLocale(a: string | null, b: string | null): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

export function localeDisplayName(locale: string, uiLanguage: string): string {
  try {
    const displayNames = new Intl.DisplayNames([uiLanguage], {type: 'language'});
    return displayNames.of(locale) ?? locale;
  } catch {
    return locale;
  }
}

/**
 * Builds the chip list for a podcast or episode from the locales the API reports as
 * available. Duplicates and unparsable tags are dropped; the source language is listed
 * first, the rest keep the API order. When two locales share a language (`de-DE`/`de-AT`)
 * the region is kept in the chip text so the chips stay distinguishable.
 */
export function buildContentLanguageOptions(
  locales: readonly string[] | null | undefined,
  selected: string | null | undefined,
  source: string | null | undefined,
  uiLanguage: string,
): ContentLanguageOption[] {
  const normalizedSource = normalizeLocaleTag(source);
  const normalizedSelected = normalizeLocaleTag(selected);

  const unique: string[] = [];
  for (const raw of [normalizedSource, ...(locales ?? [])]) {
    const locale = normalizeLocaleTag(raw);
    if (locale && !unique.some((known) => sameLocale(known, locale))) {
      unique.push(locale);
    }
  }

  const languageCounts = new Map<string, number>();
  for (const locale of unique) {
    const language = languageOf(locale);
    languageCounts.set(language, (languageCounts.get(language) ?? 0) + 1);
  }

  return unique.map((locale) => {
    const language = languageOf(locale);
    const ambiguous = (languageCounts.get(language) ?? 0) > 1;
    return {
      locale,
      short: (ambiguous ? locale : language).toUpperCase(),
      label: localeDisplayName(locale, uiLanguage),
      isSource: sameLocale(locale, normalizedSource),
      isSelected: sameLocale(locale, normalizedSelected),
    };
  });
}
